import React, { useState, useEffect, useRef } from 'react';
import { Check, X, HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { QuizData } from '../content-editor/types';
import CustomImageViewer from '@/components/ui/CustomImageViewer';
import ReactDOM from 'react-dom/client';

interface QuizQuestionProps {
  quiz: QuizData;
  onComplete: (score: number, total: number) => void;
  className?: string;
  disabled?: boolean;
}

// Helper function to diagnose rendering issues (only used in development)
const debugElement = (element: HTMLElement | null, level = 0): string => {
  if (!element) return 'null';
  
  const indent = '  '.repeat(level);
  let output = `${indent}<${element.tagName.toLowerCase()}`;
  
  // Add class and id if available
  if (element.className) output += ` class="${element.className}"`;
  if (element.id) output += ` id="${element.id}"`;
  
  output += '>\n';
  
  // Add children
  Array.from(element.children).forEach(child => {
    output += debugElement(child as HTMLElement, level + 1);
  });
  
  // Add text content if leaf node
  if (element.children.length === 0 && element.textContent?.trim()) {
    output += `${indent}  ${element.textContent.trim()}\n`;
  }
  
  output += `${indent}</${element.tagName.toLowerCase()}>\n`;
  return output;
};

const QuizQuestion: React.FC<QuizQuestionProps> = ({ 
  quiz, 
  onComplete,
  className,
  disabled = false
}) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [completed, setCompleted] = useState(false);
  
  const question = quiz.questions[currentQuestion];
  const isLastQuestion = currentQuestion === quiz.questions.length - 1;
  const hasMultipleCorrectAnswers = question?.options.filter(o => o.isCorrect).length > 1;
  
  // Process question text with images and formatting
  const processQuestionText = (text: string): string => {
    if (!text) return '';
    
    // Find image markdown pattern
    if (text.includes('![')) {
      // Process images first - use placeholders that we'll replace with components
      let processedContent = text.replace(/!\[(.+?)\]\((.+?)\)/g, (match, alt, src) => {
        // Generate a unique ID for this image container
        const imgId = `quiz-img-${Math.random().toString(36).substring(2, 11)}`;
        // Return a placeholder div that will be replaced with the CustomImageViewer
        return `<div id="${imgId}" class="quiz-image-placeholder" data-src="${encodeURIComponent(src)}" data-alt="${encodeURIComponent(alt || 'Image')}"></div>`;
      });
      
      // Then process other markdown
      processedContent = processedContent
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*]+)\*/g, '<em>$1</em>')
        .replace(/__(.+?)__/g, '<u>$1</u>')
        .replace(/\n\n/g, '<br><br>')
        .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">$1</a>');
      
      return processedContent;
    }
    
    // If no images, just process regular markdown
    return text
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*([^*]+)\*/g, '<em>$1</em>')
      .replace(/__(.+?)__/g, '<u>$1</u>')
      .replace(/\n\n/g, '<br><br>')
      .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">$1</a>');
  };

  useEffect(() => {
    if (!submitted) {
      setAnswers({});
    }
  }, [currentQuestion, submitted]);

  // Reset everything if the quiz changes
  useEffect(() => {
    setCurrentQuestion(0);
    setAnswers({});
    setSubmitted(false);
    setScore(0);
    setCompleted(false);
  }, [quiz]);

  // Check if quiz is already completed when component mounts
  useEffect(() => {
    if (disabled) {
      setCompleted(true);
    }
  }, [disabled]);

  const isSelected = (optionId: string) => {
    return answers[question.id]?.includes(optionId) || false;
  };

  const handleSelectAnswer = (optionId: string) => {
    if (submitted || disabled) return;
    
    const questionId = question.id;
    const currentAnswers = answers[questionId] || [];
    
    if (hasMultipleCorrectAnswers) {
      const newAnswers = currentAnswers.includes(optionId)
        ? currentAnswers.filter(id => id !== optionId)
        : [...currentAnswers, optionId];
      
      setAnswers({
        ...answers,
        [questionId]: newAnswers
      });
    } else {
      setAnswers({
        ...answers,
        [questionId]: [optionId]
      });
    }
  };

  const isAnswered = () => {
    return !!answers[question.id]?.length;
  };

  const handleSubmit = () => {
    if (!isAnswered() || disabled) return;
    
    const selectedOptionIds = answers[question.id] || [];
    const correctOptionIds = question.options
      .filter(o => o.isCorrect)
      .map(o => o.id);
    
    let currentQuestionCorrect = false;
    
    if (!hasMultipleCorrectAnswers) {
      currentQuestionCorrect = correctOptionIds.includes(selectedOptionIds[0]);
    } 
    else {
      currentQuestionCorrect = selectedOptionIds.every(id => correctOptionIds.includes(id)) 
        && correctOptionIds.every(id => selectedOptionIds.includes(id));
    }

    // Only update score if the answer is correct
    if (currentQuestionCorrect) {
      setScore(prevScore => prevScore + 1); 
    }
    
    setSubmitted(true);

    // If this is the last question, trigger completion immediately after submit
    if (isLastQuestion) {
      if (!completed) {
        setCompleted(true);
        // Calculate final score based on the updated score state
        const finalScore = score + (currentQuestionCorrect ? 1 : 0);
        onComplete(finalScore, quiz.questions.length);
      }
    }
  };

  const handleNext = () => {
    // Removed the completion logic from here, as it's now in handleSubmit for the last question
    if (!isLastQuestion) {
      setCurrentQuestion(currentQuestion + 1);
      setSubmitted(false);
    }
    // If it IS the last question, the button text changes and nothing happens onClick 
    // because completion is handled in handleSubmit
  };

  const isCorrectAnswer = () => {
    if (!submitted) return null;
    
    const selectedOptionIds = answers[question.id] || [];
    const correctOptionIds = question.options
      .filter(o => o.isCorrect)
      .map(o => o.id);
    
    if (!hasMultipleCorrectAnswers) {
      return correctOptionIds.includes(selectedOptionIds[0]);
    } else {
      return selectedOptionIds.every(id => correctOptionIds.includes(id)) 
        && correctOptionIds.every(id => selectedOptionIds.includes(id));
    }
  };

  const isOptionCorrect = (optionId: string) => {
    return question.options.find(o => o.id === optionId)?.isCorrect || false;
  };

  const getFeedbackMessage = () => {
    if (!submitted) return null;
    
    if (isCorrectAnswer()) {
      return "Correct! Well done.";
    } else {
      return "Incorrect. Review the correct answer(s).";
    }
  };

  if (!question) {
    return (
      <Card className={cn("w-full", className)}>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <HelpCircle className="mb-2 h-12 w-12 text-muted-foreground" />
          <CardTitle className="mb-2 text-xl">No questions available</CardTitle>
          <CardDescription>
            There are no quiz questions for this lesson.
          </CardDescription>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn("w-full animate-fade-in", className)}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Question {currentQuestion + 1} of {quiz.questions.length}
          </div>
          {hasMultipleCorrectAnswers && (
            <div className="text-xs text-muted-foreground">
              (Select all that apply)
            </div>
          )}
        </div>
        <div className="question-content">
          <CardTitle 
            className="text-xl"
            dangerouslySetInnerHTML={{ 
              __html: processQuestionText(question.question) 
            }} 
            ref={(el) => {
              // Replace placeholder divs with actual CustomImageViewer components
              if (el) {
                console.log("QuizQuestion: Processing image placeholders in CardTitle");
                
                // Debug the DOM structure
                if (process.env.NODE_ENV === 'development') {
                  console.log("QuizQuestion: DOM structure of CardTitle");
                  console.log(debugElement(el));
                }
                
                const imagePlaceholders = el.querySelectorAll('.quiz-image-placeholder');
                console.log(`QuizQuestion: Found ${imagePlaceholders.length} image placeholders`);
                
                imagePlaceholders.forEach(placeholder => {
                  const encodedSrc = placeholder.getAttribute('data-src');
                  const encodedAlt = placeholder.getAttribute('data-alt');
                  
                  if (!encodedSrc) {
                    console.error("Missing src attribute in image placeholder");
                    return;
                  }
                  
                  // Decode the URL-encoded values
                  const src = decodeURIComponent(encodedSrc);
                  const alt = encodedAlt ? decodeURIComponent(encodedAlt) : 'Image';
                  
                  console.log(`QuizQuestion: Processing image placeholder with src: ${src}`);
                  
                  // Clear any existing content and render component
                  if (!placeholder.querySelector('.image-wrapper-container')) {
                    // Create container for React to render into
                    const container = document.createElement('div');
                    container.className = "quiz-image-viewer-container";
                    placeholder.innerHTML = ''; // Clear any existing content
                    placeholder.appendChild(container);
                    
                    try {
                      // Render the custom image viewer component into the container
                      const root = ReactDOM.createRoot(container);
                      root.render(
                        <CustomImageViewer 
                          src={src}
                          alt={alt}
                        />
                      );
                      console.log(`QuizQuestion: Successfully rendered image component for ${src}`);
                    } catch (e) {
                      console.error("Error rendering image in quiz question:", e);
                      
                      // Fallback to a simple image if the component fails
                      placeholder.innerHTML = `
                        <div class="quiz-image-fallback">
                          <img src="${src}" alt="${alt}" class="rounded-md max-w-full mx-auto" />
                          ${alt ? `<div class="text-sm text-center mt-1">${alt}</div>` : ''}
                        </div>
                      `;
                    }
                  } else {
                    console.log(`QuizQuestion: Placeholder already has an image component, skipping`);
                  }
                });
              }
            }}
          />
        </div>
      </CardHeader>
      <CardContent>
        {hasMultipleCorrectAnswers ? (
          <div className="space-y-3">
            {question.options.map((option) => (
              <div
                key={option.id}
                className={cn(
                  "flex items-center space-x-3 rounded-md border p-4 transition-colors",
                  isSelected(option.id) && !submitted && "border-primary bg-primary/5",
                  submitted && isOptionCorrect(option.id) && "border-green-400 bg-green-50",
                  submitted && isSelected(option.id) && !isOptionCorrect(option.id) && "border-red-400 bg-red-50"
                )}
              >
                <Checkbox
                  id={option.id}
                  checked={isSelected(option.id)}
                  onCheckedChange={() => handleSelectAnswer(option.id)}
                  disabled={submitted || disabled}
                  className={cn(
                    submitted && isOptionCorrect(option.id) && "border-green-500 text-green-500",
                    submitted && isSelected(option.id) && !isOptionCorrect(option.id) && "border-red-500 text-red-500"
                  )}
                />
                <Label
                  htmlFor={option.id}
                  className={cn(
                    "flex-grow cursor-pointer font-normal",
                    submitted && isOptionCorrect(option.id) && "text-green-600",
                    submitted && isSelected(option.id) && !isOptionCorrect(option.id) && "text-red-600"
                  )}
                >
                  {option.text}
                </Label>
                {submitted && (
                  <div className="ml-2">
                    {isOptionCorrect(option.id) ? (
                      <Check className="h-5 w-5 text-green-500" />
                    ) : isSelected(option.id) ? (
                      <X className="h-5 w-5 text-red-500" />
                    ) : null}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <RadioGroup
            value={answers[question.id]?.[0] || ""}
            onValueChange={(value) => handleSelectAnswer(value)}
            className="space-y-3"
            disabled={submitted || disabled}
          >
            {question.options.map((option) => (
              <div
                key={option.id}
                className={cn(
                  "flex items-center space-x-3 rounded-md border p-4 transition-colors",
                  answers[question.id]?.[0] === option.id && !submitted && "border-primary bg-primary/5",
                  submitted && isOptionCorrect(option.id) && "border-green-400 bg-green-50",
                  submitted && answers[question.id]?.[0] === option.id && !isOptionCorrect(option.id) && "border-red-400 bg-red-50"
                )}
              >
                <RadioGroupItem
                  value={option.id}
                  id={option.id}
                  className={cn(
                    submitted && isOptionCorrect(option.id) && "border-green-500 text-green-500",
                    submitted && answers[question.id]?.[0] === option.id && !isOptionCorrect(option.id) && "border-red-500 text-red-500"
                  )}
                />
                <Label
                  htmlFor={option.id}
                  className={cn(
                    "flex-grow cursor-pointer font-normal",
                    submitted && isOptionCorrect(option.id) && "text-green-600",
                    submitted && answers[question.id]?.[0] === option.id && !isOptionCorrect(option.id) && "text-red-600"
                  )}
                >
                  {option.text}
                </Label>
                {submitted && (
                  <div className="ml-2">
                    {isOptionCorrect(option.id) ? (
                      <Check className="h-5 w-5 text-green-500" />
                    ) : answers[question.id]?.[0] === option.id ? (
                      <X className="h-5 w-5 text-red-500" />
                    ) : null}
                  </div>
                )}
              </div>
            ))}
          </RadioGroup>
        )}
      </CardContent>
      <CardFooter className="flex flex-col space-y-4">
        {submitted && (
          <div
            className={cn(
              "w-full rounded-md p-3 text-center font-medium",
              isCorrectAnswer()
                ? "bg-green-50 text-green-600"
                : "bg-red-50 text-red-600"
            )}
          >
            {isCorrectAnswer() ? (
              <span className="flex items-center justify-center">
                <Check className="mr-2 h-5 w-5" />
                {getFeedbackMessage()}
              </span>
            ) : (
              <span className="flex items-center justify-center">
                <X className="mr-2 h-5 w-5" />
                {getFeedbackMessage()}
              </span>
            )}
          </div>
        )}
        <div className="flex w-full justify-between">
          {!submitted ? (
            <Button onClick={handleSubmit} disabled={!isAnswered() || disabled} className="w-full">
              Submit Answer
            </Button>
          ) : (
            <Button onClick={handleNext} className="w-full">
              {isLastQuestion ? "Complete Quiz" : "Next Question"}
            </Button>
          )}
        </div>
      </CardFooter>
    </Card>
  );
};

export default QuizQuestion;
